import React, { useState, memo } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
type FAQItemProps = {
  question: string;
  answer: React.ReactNode;
};
function FAQItem({
  question,
  answer
}: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg overflow-hidden mb-4" data-id="element-94">
      <button className="w-full flex justify-between items-center p-6 bg-white hover:bg-gray-50 transition-colors text-left" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} data-id="element-95">
        <h3 className="text-lg font-semibold text-gray-800" data-id="element-96">{question}</h3>
        <span className="text-indigo-600 ml-4" data-id="element-97">
          {isOpen ? <ChevronUpIcon size={20} data-id="element-98" /> : <ChevronDownIcon size={20} data-id="element-99" />}
        </span>
      </button>
      {isOpen && <div className="p-6 bg-gray-50 border-t border-gray-200" data-id="element-100">
          <div className="text-gray-700" data-id="element-101">{answer}</div>
        </div>}
    </div>;
}
export function FAQ() {
  return <section id="faq" className="py-20 bg-white" data-id="element-102">
      <div className="container mx-auto px-4" data-id="element-103">
        <div className="text-center mb-16" data-id="element-104">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-105">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-106">
            Find answers to common questions about our music collective and how
            to get involved.
          </p>
        </div>
        <div className="max-w-3xl mx-auto" data-id="element-107">
          <FAQItem question="Does this group exist?" answer={<p data-id="element-109">
                Not yet. This list is the first step to demonstrate interest.
                We're actively seeking an agency who would serve as the "anchor"
                for this group: providing facilities and possibly staff to make
                it a reality.
              </p>} data-id="element-108" />
          <FAQItem question="What is the purpose of Special Performances?" answer={<p data-id="element-111">
                Special Performances will be a music collective specifically
                designed for adult musicians with autism and intellectual
                disabilities. Our goal is to create a supportive environment
                where these musicians can develop their talents, collaborate
                with others, and showcase their unique musical perspectives
                through regular performances and events.
              </p>} data-id="element-110" />
          <FAQItem question="Who is this group designed for?" answer={<>
                <p className="mb-3" data-id="element-113">Our collective is designed for:</p>
                <ul className="list-disc pl-5 mb-3 space-y-1" data-id="element-114">
                  <li data-id="element-115">
                    Adult musicians with autism or intellectual disabilities
                  </li>
                  <li data-id="element-116">Musicians with some existing musical abilities</li>
                  <li data-id="element-117">
                    People who are interested in collaborative music-making
                  </li>
                  <li data-id="element-118">
                    Individuals who want to develop their musical talents in a
                    supportive environment
                  </li>
                </ul>
                <p data-id="element-119">
                  We welcome musicians of various skill levels, but participants
                  should have some basic musical abilities and an interest in
                  developing them further.
                </p>
              </>} data-id="element-112" />
          <FAQItem question="What is this group NOT designed for?" answer={<>
                <p className="mb-3" data-id="element-121">To be clear, our collective is:</p>
                <ul className="list-disc pl-5 mb-3 space-y-1" data-id="element-122">
                  <li data-id="element-123">Not a music therapy program</li>
                  <li data-id="element-124">Not a beginner's music class</li>
                  <li data-id="element-125">
                    Not specifically designed for children (we focus on adult
                    musicians)
                  </li>
                  <li data-id="element-126">Not a traditional music education program</li>
                </ul>
                <p data-id="element-127">
                  While we provide support and guidance, our primary focus is on
                  collaborative music-making and performance opportunities
                  rather than therapeutic outcomes or basic music education.
                </p>
              </>} data-id="element-120" />
          <FAQItem question="Is this an alternative to day hab or prevocational training?" answer={<p data-id="element-129">
                It very well could be. Our concept is to provide a meaningful
                and engaging alternative to day hab for high-functioning
                individuals that also have musical abilities. Given the right
                supports, these participants could also find employment
                opportunities in the performing arts.
              </p>} data-id="element-128" />
          <FAQItem question="What happens if you can't find an agency to house the program?" answer={<p data-id="element-131">
                We believe this sort of group would be a great social
                opportunity for musicians to meet and jam. We also see potential
                for a group to provide free concerts at nursing homes
                (especially memory care facilities), retirement communities,
                schools, and public festivals.
              </p>} data-id="element-130" />
          <FAQItem question="What musical experience is required?" answer={<p data-id="element-133">
                Participants should have some existing musical abilities, such
                as basic instrumental or vocal skills. Members should be able to
                participate meaningfully in group music-making. If you're unsure
                whether your skills or your family member's skills are a good
                fit, please reach out to us through the contact form and to
                arrange an audition or performance review.
              </p>} data-id="element-132" />
          <FAQItem question="How will the sessions work?" answer={<>
                <p className="mb-3" data-id="element-135">Here's our concept:</p>
                <ul className="list-disc pl-5 mb-3 space-y-1" data-id="element-136">
                  <li data-id="element-137">Regular weekly group practice sessions of 90 minutes</li>
                  <li data-id="element-138">Collaborative music projects</li>
                  <li data-id="element-139">Performance opportunities</li>
                  <li data-id="element-140">Workshops focused on developing specific skills</li>
                </ul>
                <p data-id="element-141">
                  We're planning on sessions led by experienced mentors
                  (professional music educators, music therapists, or musicians
                  with teaching experience). We need caring mentors who
                  understand the unique needs (and rare talent) of musicians
                  with autism and intellectual disabilities.
                </p>
              </>} data-id="element-134" />
          <FAQItem question="Is there any cost to participate?" answer={<p data-id="element-143">
                Right now we're seeking interested musicians. We'll discuss a
                shared-cost model once we get a critical mass of participants.
              </p>} data-id="element-142" />
          <FAQItem question="Where will this happen?" answer={<p data-id="element-145">
                Right now we're looking for musicians within a 30 minute drive
                of Cherry Hill, NJ.
              </p>} data-id="element-144" />
          <FAQItem question="Why should I enroll a family member?" answer={<p data-id="element-147">
                To start this process, we need to recruit an agency who wants to
                provide facilities and perhaps even facilliators. So, the more
                interested people we get, the faster we can move forward. We'll
                actively lobby NJ DDD and registered agencies to find an
                "anchor" sponsor.
              </p>} data-id="element-146" />
        </div>
      </div>
    </section>;
}